import { useRef, useState } from "react";
import { Bold, Italic, Link2, Eye, EyeOff } from "lucide-react";
import { RichText } from "./RichText";

// Editor ricco stile Discord/social per i testi del sito: grassetto, corsivo,
// link, COLORI (anche piu' colori nello stesso testo: selezioni col mouse e
// applichi), a capo libero con righe di spazio. Il riquadro ha altezza fissa e
// scorre con la rotellina quando il testo e' lungo. Anteprima live sotto.
export const RichTextEditor = ({ label, id, value, onChange, hint = "", compact = false }) => {
  const ref = useRef(null);
  const [showPreview, setShowPreview] = useState(false);

  // avvolge la selezione corrente (o inserisce al cursore) mantenendo il focus
  const wrap = (before, after = before) => {
    const el = ref.current;
    if (!el) return;
    const s = el.selectionStart ?? value.length;
    const e = el.selectionEnd ?? value.length;
    const next = value.slice(0, s) + before + value.slice(s, e) + after + value.slice(e);
    onChange(next);
    requestAnimationFrame(() => {
      el.focus();
      el.setSelectionRange(s + before.length, e + before.length);
    });
  };

  const addLink = () => {
    const url = window.prompt("Incolla l'indirizzo del link (https://…)");
    if (!url) return;
    wrap("[", `](${url.trim()})`);
  };

  const addColor = (hex) => wrap(`<span style="color:${hex}">`, "</span>");

  const btn = "chamfer-sm border border-[#1A1A1A] hover:border-wardogs/50 hover:text-white text-steel p-2 transition-colors duration-300 flex items-center justify-center";

  return (
    <div data-testid={`${id}-editor`}>
      <label className="hud-label block mb-2" htmlFor={id}>{label}</label>
      {hint && <p className="font-mono text-[10px] text-[#4A4D51] mb-2 leading-relaxed">{hint}</p>}
      <div className="flex items-center gap-1.5 mb-2 flex-wrap">
        <button type="button" title="Grassetto (**testo**)" onClick={() => wrap("**")} className={btn} data-testid={`${id}-bold`}><Bold className="w-3.5 h-3.5" /></button>
        <button type="button" title="Corsivo (*testo*)" onClick={() => wrap("*")} className={btn} data-testid={`${id}-italic`}><Italic className="w-3.5 h-3.5" /></button>
        <button type="button" title="Link — seleziona il testo e incolla l'URL" onClick={addLink} className={btn} data-testid={`${id}-link`}><Link2 className="w-3.5 h-3.5" /></button>
        <label title="Colore — seleziona il testo col mouse e scegli il colore"
          className={`${btn} cursor-pointer relative overflow-hidden`} data-testid={`${id}-color`}>
          <span className="w-3.5 h-3.5 chamfer-sm border border-white/30" style={{ background: "linear-gradient(135deg,#FF5C00 0%,#3DDC84 50%,#4A9EFF 100%)" }} />
          <input type="color" defaultValue="#FF5C00" onChange={(e) => addColor(e.target.value)}
            className="absolute inset-0 opacity-0 cursor-pointer" data-testid={`${id}-color-input`} />
        </label>
        <span className="font-mono text-[9px] text-[#4A4D51] ml-1 hidden md:inline">**grassetto** · *corsivo* · [testo](link) · doppio invio = riga di spazio</span>
        <button type="button" onClick={() => setShowPreview(!showPreview)}
          className="ml-auto font-mono text-[9px] uppercase tracking-[0.15em] text-steel hover:text-wardogs transition-colors duration-300 flex items-center gap-1.5"
          data-testid={`${id}-preview-toggle`}>
          {showPreview ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
          {showPreview ? "Nascondi anteprima" : "Anteprima"}
        </button>
      </div>
      <textarea
        ref={ref}
        id={id}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="Vuoto = questa riga non compare sul sito"
        rows={compact ? 2 : 5}
        data-lenis-prevent
        className={`w-full bg-[#050505] border border-[#1A1A1A] focus:border-wardogs outline-none px-4 py-3 font-mono text-sm text-white transition-colors duration-300 overflow-y-auto overscroll-contain resize-y ${compact ? "max-h-40" : "min-h-28 max-h-72"}`}
        data-testid={`${id}-input`}
      />
      {showPreview && (
        <div className="mt-2 chamfer-sm border border-wardogs/25 bg-[#050505] px-4 py-3" data-testid={`${id}-preview`}>
          <p className="font-mono text-[9px] uppercase tracking-[0.2em] text-wardogs mb-2">Anteprima — così apparirà sul sito</p>
          {value.trim()
            ? <RichText tag="div" text={value} className="font-mono text-sm text-[#C9C9CE] leading-relaxed" />
            : <p className="font-mono text-[10px] text-[#4A4D51]">(vuoto: questa riga resterà nascosta sul sito)</p>}
        </div>
      )}
    </div>
  );
};

export default RichTextEditor;
